import React, { useEffect, useState } from 'react';
import { BadgeDollarSign, Trophy } from 'lucide-react';
import { fetchLeaderboard, LeaderboardEntry, TopSpender } from '../api';
import { fetchUserProfilePic, getDefaultAvatarUrl } from '../utils/profilePicCache';
import LoadingIndicator from './LoadingIndicator';

interface LeaderboardCardProps {
  onOpen: () => void;
}

const LeaderboardCard: React.FC<LeaderboardCardProps> = ({ onOpen }) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [topSpender, setTopSpender] = useState<TopSpender | null>(null);
  const [profilePics, setProfilePics] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const normalizeName = (name: string) => String(name || '').trim().toLowerCase();

  useEffect(() => {
    let cancelled = false;

    async function loadTopFive() {
      setLoading(true);
      const data = await fetchLeaderboard(1);
      if (!cancelled) {
        setEntries(data.data.slice(0, 5));
        setTopSpender(data.topSpender || null);
        setLoading(false);
      }
    }

    loadTopFive();
    const timer = window.setInterval(loadTopFive, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchProfilePics() {
      const names = entries.map(entry => entry.name).filter(Boolean);
      const uniqueNames = Array.from(new Set(names));

      for (const name of uniqueNames) {
        if (profilePics[name]) {
          continue;
        }

        try {
          const url = await fetchUserProfilePic(name);
          if (!cancelled) {
            setProfilePics(prev => ({ ...prev, [name]: url }));
          }
        } catch (error) {
          console.error(`Failed to fetch profile pic for ${name}:`, error);
        }
      }
    }

    fetchProfilePics();

    return () => {
      cancelled = true;
    };
  }, [entries, profilePics]);

  function getAvatarRing(index: number) {
    if (index === 0) return 'ring-2 ring-yellow-400/80 shadow-[0_0_18px_rgba(250,204,21,0.28)]';
    if (index === 1) return 'ring-2 ring-zinc-300/80 shadow-[0_0_18px_rgba(212,212,216,0.22)]';
    if (index === 2) return 'ring-2 ring-amber-700/80 shadow-[0_0_18px_rgba(180,83,9,0.25)]';
    return 'ring-1 ring-white/10';
  }

  return (
    <div className="glass-card p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
        <h2 className="flex min-w-0 items-center gap-2 text-lg font-semibold md:text-xl">
          <Trophy className="text-white/70" size={22} />
          Point Leaderboard
        </h2>
        <button
          type="button"
          onClick={onOpen}
          className="h-8 rounded-full bg-white/10 px-3 text-xs text-white/90 transition hover:bg-white/20 active:scale-[0.98] md:text-sm"
        >
          Megnyitás
        </button>
      </div>

      {loading ? (
        <div className="flex min-h-[190px] items-center justify-center">
          <LoadingIndicator compact />
        </div>
      ) : entries.length ? (
        <div className="space-y-2 md:space-y-3">
          {topSpender && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-300/20 bg-emerald-300/[0.08] px-3 py-2 text-xs text-emerald-100">
              <div className="flex min-w-0 items-center gap-2">
                <BadgeDollarSign size={15} className="shrink-0 text-emerald-200" />
                <span className="truncate font-semibold">Top spender: {topSpender.user_name}</span>
              </div>
              <span className="shrink-0 font-bold tabular-nums">{topSpender.count}x</span>
            </div>
          )}

          {entries.map((entry, index) => {
            const isTopSpender = topSpender?.normalized === normalizeName(entry.name);

            return (
              <div
                key={`${entry.name}-${entry.rank || index}`}
                className={`flex items-center justify-between gap-2.5 rounded-[3rem] bg-white/5 px-2.5 py-2 backdrop-blur-sm transition-colors duration-200 hover:bg-white/10 md:gap-3 md:px-3 ${index === 0 ? 'ring-1 ring-white/20' : ''}`}
                style={{
                  boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1), 0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="flex min-w-0 items-center gap-2.5 md:gap-3">
                  <div className={`relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white/10 md:h-10 md:w-10 ${getAvatarRing(index)}`}>
                    <img
                      src={profilePics[entry.name] || getDefaultAvatarUrl(entry.name)}
                      alt={entry.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = getDefaultAvatarUrl(entry.name);
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium text-white/90">{entry.name}</span>
                      {isTopSpender && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-300/20 bg-emerald-300/[0.1] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-100">
                          <BadgeDollarSign size={11} />
                          Spender
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/50">#{entry.rank || index + 1}</div>
                  </div>
                </div>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-white/80 md:text-sm">
                  {entry.points.toLocaleString('en-US')}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex min-h-[190px] items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm text-white/45">
          Nincs leaderboard adat
        </div>
      )}
    </div>
  );
};

export default React.memo(LeaderboardCard);
